import {
  ELIMINATION_TIER_POINTS, ELIMINATION_TIER_LABELS, ELIMINATION_TIER_FALLBACK, ELIMINATION_TIER_FALLBACK_LABEL,
} from "../utils/sweepstakes";

const FLAIR = { from: "#66BB6A", to: "#42A5F5", solid: "#4CAF50" };
const PLACE_LABELS = ["1st", "2nd", "3rd"];
// index 0 = 1st (gold), 1 = 2nd (silver), 2 = 3rd (bronze)
const MEDAL_COLORS = ["#FFD54F", "#B0BEC5", "#D7A86E"];
const BLOCK_HEIGHTS = ["h-32", "h-24", "h-20"];
// visual left-to-right order on the podium: 2nd, 1st, 3rd
const PODIUM_ORDER = [1, 0, 2];

/**
 * SweepstakesPodium — top-3 school ranking as a medal-stand podium, shown at the top of
 * the event's SWEEPS RESULTS tab.
 * Props: schools: [...from calculateSweepstakes(...).schools]
 */
function SweepstakesPodium({ schools }) {
  const top3 = schools.slice(0, 3);

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-6">
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4 text-center">SWEEPSTAKES — TOP 3 SCHOOLS</div>
      <div className="flex items-end justify-center gap-3">
        {PODIUM_ORDER.filter((i) => top3[i]).map((i) => {
          const s = top3[i];
          const color = MEDAL_COLORS[i];
          return (
            <div key={s.school} className="flex flex-col items-center w-1/3 max-w-[150px]">
              <span className="text-sm font-semibold text-gray-200 text-center leading-tight mb-1">{s.school}</span>
              <span className="text-[10px] text-gray-600 mb-2">{s.fighterCount} fighter{s.fighterCount !== 1 ? "s" : ""}</span>
              <div className={`w-full ${BLOCK_HEIGHTS[i]} rounded-t-lg flex flex-col items-center justify-start pt-2.5`}
                style={{ background: `linear-gradient(180deg, ${color}26, transparent)`, borderTop: `3px solid ${color}` }}>
                <span className="font-display text-xs font-extrabold tracking-wider" style={{ color }}>{PLACE_LABELS[i]}</span>
                <span className="font-display text-2xl font-extrabold text-white mt-1">{s.totalPoints}</span>
                <span className="text-[9px] text-gray-500 tracking-wider">POINTS</span>
                <span className="text-[9px] text-gray-600 mt-1.5">{s.poolPoints} pool + {s.eliminationPoints} bracket</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * SweepstakesInfo — explanation of the sweepstakes formula and the elimination bonus
 * scale, shown on the event's SWEEPS INFO tab.
 */
export function SweepstakesInfo() {
  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">HOW SWEEPSTAKES POINTS ARE CALCULATED</div>
      <p className="text-gray-400 text-sm mb-4">
        Every fighter earns points for their whole-tournament performance across every discipline
        they compete in, and those points are summed by school (affiliation) to rank schools for
        the sweepstakes award. A fighter's total is their pool points plus their elimination bonus
        from each discipline — pool performance always counts toward their school's total, even
        for fighters who don't make the bracket.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-bold text-gray-500 tracking-wider mb-1.5">POOL POINTS</div>
          <p className="text-gray-500 text-xs">
            Each fighter's P/I (win = 3, tie = 1, loss = 0) from the round-robin pool stage,
            normalized against the average pool size — so a fighter in a smaller pool isn't
            penalized for fencing fewer bouts than one in a bigger pool.
          </p>
        </div>
        <div>
          <div className="text-xs font-bold text-gray-500 tracking-wider mb-1.5">ELIMINATION BONUS</div>
          <p className="text-gray-500 text-xs mb-2">Added on top of pool points for fighters who make the bracket, based on how far they got:</p>
          <table className="w-full text-xs">
            <caption className="sr-only">Elimination placement points</caption>
            <thead><tr><th scope="col" className="sr-only">Placement</th><th scope="col" className="sr-only">Points</th></tr></thead>
            <tbody>
              {ELIMINATION_TIER_LABELS.map((label, i) => (
                <tr key={label} className="border-t border-dark-500">
                <th scope="row" className="py-1 text-left font-normal text-gray-400">{label}</th>
                  <td className="py-1 text-right font-display font-bold" style={{ color: FLAIR.solid }}>+{ELIMINATION_TIER_POINTS[i]}</td>
                </tr>
              ))}
              <tr className="border-t border-dark-500">
              <th scope="row" className="py-1 text-left font-normal text-gray-400">{ELIMINATION_TIER_FALLBACK_LABEL}</th>
                <td className="py-1 text-right font-display font-bold" style={{ color: FLAIR.solid }}>+{ELIMINATION_TIER_FALLBACK}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * SweepstakesResults — podium plus every school's ranking with a per-fighter breakdown,
 * shown on the event's SWEEPS RESULTS tab.
 * Props: schools: [...from calculateSweepstakes(...).schools]
 */
export default function SweepstakesResults({ schools }) {
  if (!schools || schools.length === 0) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-8 text-center">
        <div className="font-display text-sm font-bold text-gray-400 mb-1">No sweepstakes data yet</div>
        <p className="text-gray-600 text-xs max-w-sm mx-auto">Results appear once pool bouts have been confirmed.</p>
      </div>
    );
  }

  return (
    <div>
      <SweepstakesPodium schools={schools} />
      <div className="flex flex-col gap-3">
        {schools.map((s) => (
          <div key={s.school} className="bg-dark-700 border border-dark-500 rounded-xl p-4">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="font-display text-sm font-extrabold text-gray-500 shrink-0">{`#${s.rank}`}</span>
                <span className="text-sm font-semibold text-gray-200 truncate">{s.school}</span>
                <span className="text-[10px] text-gray-600 shrink-0">({s.fighterCount} fighter{s.fighterCount !== 1 ? "s" : ""})</span>
              </div>
              <span className="font-display text-sm font-extrabold shrink-0" style={{ color: FLAIR.solid }}>{s.totalPoints} pts</span>
            </div>
            <div className="text-[10px] text-gray-600 mb-2">{s.poolPoints} pool pts + {s.eliminationPoints} bracket pts</div>
            <div className="flex flex-col gap-1 pt-2 border-t border-dark-500">
              {s.fighters.map((f) => (
                <div key={f.name} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 truncate">{f.name}</span>
                  <span className="text-gray-600 shrink-0 ml-2">
                    {f.poolPoints} + {f.eliminationPoints} = <span className="text-gray-300 font-semibold">{f.totalPoints}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
